export type AppEntryAssetCache = 'warm' | 'partial' | 'cold' | 'unknown';

export type AppEntryAssetCaches = {
  wandb?: AppEntryAssetCache;
  shell?: AppEntryAssetCache;
};

export type AppEntryResourceTiming = Pick<
  PerformanceResourceTiming,
  'name' | 'transferSize' | 'encodedBodySize'
> & {
  deliveryType?: string;
};

const APP_ENTRY_ORIGIN = new URL(import.meta.url).origin;
export const WANDB_ASSET_BASE = new URL('..', import.meta.url).pathname;
// 250 is the Resource Timing spec's default buffer size.
const RESOURCE_TIMING_BUFFER_SIZE = 250;

export function isWandbAsset(url: URL): boolean {
  return (
    url.origin === APP_ENTRY_ORIGIN &&
    url.pathname.startsWith(WANDB_ASSET_BASE) &&
    !url.pathname.endsWith('/env.shell.js')
  );
}

export function isShellAsset(url: URL): boolean {
  return (
    url.origin === APP_ENTRY_ORIGIN &&
    (url.pathname.startsWith('/shell-assets/') ||
      /^\/remoteEntry-[^/]+\.js$/.test(url.pathname))
  );
}

export function classifyAssetCache(
  entries: ReadonlyArray<AppEntryResourceTiming>,
  belongs: (url: URL) => boolean
): AppEntryAssetCache | undefined {
  let knownCount = 0;
  let downloadedCount = 0;
  let unknownCount = 0;

  for (const entry of entries) {
    let url: URL;
    try {
      url = new URL(entry.name);
    } catch {
      continue;
    }

    if (!url.pathname.endsWith('.js') || !belongs(url)) {
      continue;
    }

    if (entry.deliveryType === 'cache') {
      knownCount++;
    } else if (entry.encodedBodySize > 0) {
      knownCount++;
      if (entry.transferSize > entry.encodedBodySize) {
        downloadedCount++;
      }
    } else {
      unknownCount++;
    }
  }

  if (knownCount === 0) {
    return unknownCount > 0 ? 'unknown' : undefined;
  }

  const downloadedRatio = downloadedCount / knownCount;
  if (downloadedRatio < 0.1) {
    return 'warm';
  }
  if (downloadedRatio > 0.9) {
    return 'cold';
  }
  return 'partial';
}

let appEntryAssetCaches: AppEntryAssetCaches | undefined;
let captured = false;

export function captureAppEntryAssetCache({shell}: {shell: boolean}): void {
  if (captured) {
    return;
  }
  captured = true;

  if (
    typeof performance === 'undefined' ||
    typeof performance.getEntriesByType !== 'function'
  ) {
    return;
  }

  // Static imports have loaded by app entry; later Datadog RUM initialization
  // calls performance.clearResourceTimings() when the ~250-entry buffer fills.
  // This sample is sufficient to estimate the entry JavaScript chunk ratio.
  const entries = performance.getEntriesByType(
    'resource'
  ) as PerformanceResourceTiming[];
  const bufferFull = entries.length >= RESOURCE_TIMING_BUFFER_SIZE;
  const classify = (belongs: (url: URL) => boolean) =>
    classifyAssetCache(entries, belongs) ??
    (bufferFull ? 'unknown' : undefined);
  const caches: AppEntryAssetCaches = {};
  const wandb = classify(isWandbAsset);

  if (wandb !== undefined) {
    caches.wandb = wandb;
  }

  if (shell) {
    const shellCache = classify(isShellAsset);
    if (shellCache !== undefined) {
      caches.shell = shellCache;
    }
  }

  appEntryAssetCaches =
    caches.wandb !== undefined || caches.shell !== undefined
      ? caches
      : undefined;
}

export function takeAppEntryAssetCache(): AppEntryAssetCaches | undefined {
  const assetCaches = appEntryAssetCaches;
  appEntryAssetCaches = undefined;
  return assetCaches;
}
